"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  Panel,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  type AaporResultLabels,
  AaporResults,
} from "@/components/tools/research-ops/aapor-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { percentText } from "@/lib/tool-kit/research-design/format";
import {
  DISPOSITION_KEYS,
  outcomeRates,
  type Rate,
  totalCases,
} from "@/lib/tool-kit/research-ops/aapor";
import {
  eligibleCases,
  type FieldKey,
  type Fields,
  type ResolvedE,
  readCounts,
  resolveE,
} from "@/lib/tool-kit/research-ops/aapor-input";

/** English strings. A translated content file overrides these through `ui`. */
export const SURVEY_RESPONSE_RATE_CALCULATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  dispositionsHeading: "Final dispositions",
  dispositionsHint:
    "Count each sampled case once, by its final disposition. AAPOR's code numbers are shown. Leave a field blank for zero.",
  dispositionLabels: {
    I: "Complete interviews (I) — 1.1",
    P: "Partial interviews (P) — 1.2",
    R: "Refusals and break-offs (R) — 2.10",
    NC: "Non-contacts (NC) — 2.20",
    O: "Other eligible non-interviews (O) — 2.30, 2.90",
    UH: "Unknown if household or unit exists (UH) — 3.10",
    UR: "Unknown if sampled unit is eligible (UR) — 3.20",
    UO: "Unknown, other (UO) — 3.90",
  },
  ineligibleLabel: "Ineligible cases (category 4), optional",
  ineligibleHint:
    "Out-of-scope cases such as non-working numbers or non-residences. They are in no rate; they are used only to estimate e below.",
  eHeading: "Eligibility of the unknown cases (e)",
  eHint:
    "RR3, RR4, REF2 and CON2 need e, the estimated share of unknown cases that are eligible (0 to 1). AAPOR requires you to base it on the best available evidence, state the basis, and never choose it to raise the rate.",
  eLabel: "e (a proportion from 0 to 1, or a percentage)",
  ePlaceholder: "e.g. 0.55",
  eBlankNote:
    "Leave e blank to estimate it by proportional allocation from the ineligible count above — the unknown cases are assumed eligible at the same rate as the known ones.",
  eUsedEntered: "e = {e}, as entered.",
  eUsedProportional:
    "e = {e}, estimated by proportional allocation: {eligible} known eligible of {known} cases of known status.",
  eMissing:
    "No e yet, so RR3, RR4, REF2 and CON2 are not shown. Enter e, or enter the ineligible cases to estimate it.",
  problemCount: "Counts must be whole numbers: check {fields}.",
  problemE: "e must be between 0 and 1 (or 0% and 100%).",
  totalLine: "{total} sampled cases in all.",
  prompt: "Enter the disposition counts to see the rates.",
  resultsHeading: "Outcome rates",
  groupNames: {
    response: "Response rates",
    cooperation: "Cooperation rates",
    refusal: "Refusal rates",
    contact: "Contact rates",
  },
  rateNames: {
    RR1: "RR1 — minimum",
    RR2: "RR2 — with partials",
    RR3: "RR3 — with e",
    RR4: "RR4 — with e and partials",
    RR5: "RR5 — e = 0",
    RR6: "RR6 — e = 0, with partials (maximum)",
    COOP1: "COOP1 — minimum",
    COOP2: "COOP2 — with partials",
    COOP3: "COOP3 — excluding those unable",
    COOP4: "COOP4 — excluding those unable, with partials",
    REF1: "REF1 — unknowns all eligible",
    REF2: "REF2 — with e",
    REF3: "REF3 — known eligible only",
    CON1: "CON1 — unknowns all eligible",
    CON2: "CON2 — with e",
    CON3: "CON3 — known eligible only",
  },
  colRate: "Rate",
  colFormula: "Formula",
  colWorking: "Working",
  colValue: "Value",
  needsE: "Needs e",
  noCases: "No cases in the denominator",
  rr3Note:
    "AAPOR describes RR3 as the most common response rate in reporting. Report exactly which rate you used — “the response rate was X” is not acceptable — together with the disposition counts.",
  reportHeading: "Reporting sentence",
  reportLegend: "Rate to report",
  reportSentence: "{rate} was {value}.",
  reportWithE: "{rate} was {value}, with e = {e} estimated by {basis}.",
  basisEntered: "the researcher's own estimate (basis to be stated)",
  basisProportional: "proportional allocation",
  reportRateNames: {
    RR1: "Response Rate 1",
    RR2: "Response Rate 2",
    RR3: "Response Rate 3",
    RR4: "Response Rate 4",
    RR5: "Response Rate 5",
    RR6: "Response Rate 6",
  },
  copyReport: "Copy sentence",
  referenceHeading: "Reference",
  referenceNote:
    "APA 7 form of the AAPOR standard, current as of the 10th edition (2023). Cite the edition you used.",
  reference: {
    before: "The American Association for Public Opinion Research. (2023). ",
    title:
      "Standard definitions: Final dispositions of case codes and outcome rates for surveys",
    after:
      " (10th ed.). https://aapor.org/wp-content/uploads/2023/05/Standards-Definitions-10th-edition.pdf",
  },
  copyReference: "Copy reference",
  copied: "Copied!",
  example: {
    I: "412",
    P: "38",
    R: "265",
    NC: "190",
    O: "22",
    UH: "120",
    UR: "90",
    UO: "40",
    ineligible: "140",
  },
} as const;

type Report = "RR1" | "RR2" | "RR3" | "RR4" | "RR5" | "RR6";

const REPORTS: readonly Report[] = ["RR1", "RR2", "RR3", "RR4", "RR5", "RR6"];
const BLANK: Fields = {
  I: "",
  P: "",
  R: "",
  NC: "",
  O: "",
  UH: "",
  UR: "",
  UO: "",
  ineligible: "",
};

type Strings = typeof SURVEY_RESPONSE_RATE_CALCULATOR_STRINGS;

/** The sentence to paste into a methods section, and the AAPOR reference. */
function Reporting({
  rates,
  resolved,
  strings,
}: {
  rates: readonly Rate[];
  resolved: ResolvedE;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const [report, setReport] = useState<Report>("RR3");
  const chosen = rates.find((rate) => rate.id === report);
  let sentence: string | null = null;
  if (chosen && chosen.value !== null) {
    const rate = strings.reportRateNames[report];
    const value = percentText(fmt, chosen.value, 1);
    sentence = chosen.usesE
      ? t(strings.reportWithE, {
          rate,
          value,
          e: fmt(resolved.e ?? 0, { maximumFractionDigits: 3 }),
          basis:
            resolved.source === "proportional"
              ? strings.basisProportional
              : strings.basisEntered,
        })
      : t(strings.reportSentence, { rate, value });
  }
  const { before, title, after } = strings.reference;
  return (
    <>
      <div className={`${RESULT_CARD} space-y-3`}>
        <PillGroup
          label={strings.reportLegend}
          name="rr-report"
          onChange={setReport}
          options={REPORTS.map((id) => ({
            value: id,
            label: strings.reportRateNames[id],
          }))}
          value={report}
        />
        {sentence ? (
          <>
            <p className="text-foreground text-sm">{sentence}</p>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyReport}
              text={sentence}
            />
          </>
        ) : null}
      </div>
      <div className="space-y-2">
        <h3 className="font-semibold text-foreground text-lg">
          {strings.referenceHeading}
        </h3>
        <p className="text-foreground text-sm">
          {before}
          <em>{title}</em>
          {after}
        </p>
        <p className={HINT}>{strings.referenceNote}</p>
        <CopyButton
          copiedLabel={strings.copied}
          html={`${before}<i>${title}</i>${after}`}
          label={strings.copyReference}
          text={`${before}${title}${after}`}
        />
      </div>
    </>
  );
}

export default function SurveyResponseRateCalculatorWidget() {
  const strings = useToolUi(SURVEY_RESPONSE_RATE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [fields, setFields] = useState<Fields>(BLANK);
  const [eText, setEText] = useState("");

  const counts = useMemo(() => readCounts(fields), [fields]);
  const resolved = resolveE(eText, counts);
  const { e } = resolved;
  const rates = useMemo(
    () => outcomeRates(counts.dispositions, e),
    [counts.dispositions, e]
  );
  const total = totalCases(counts.dispositions, counts.ineligible ?? 0);
  const eligible = eligibleCases(counts.dispositions);

  const badFields = counts.bad.map((key) =>
    key === "ineligible"
      ? strings.ineligibleLabel
      : strings.dispositionLabels[key]
  );
  const setField = (key: FieldKey) => (value: string) =>
    setFields((previous) => ({ ...previous, [key]: value }));
  const labels: AaporResultLabels = strings;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setFields(strings.example);
            setEText("");
          }}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setFields(BLANK);
            setEText("");
          }}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.dispositionsHeading}>
        <p className={HINT}>{strings.dispositionsHint}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          {DISPOSITION_KEYS.map((key) => (
            <TextField
              id={`rr-${key}`}
              key={key}
              label={strings.dispositionLabels[key]}
              numeric
              onChange={setField(key)}
              value={fields[key]}
            />
          ))}
        </div>
        <TextField
          hint={strings.ineligibleHint}
          id="rr-ineligible"
          label={strings.ineligibleLabel}
          numeric
          onChange={setField("ineligible")}
          value={fields.ineligible}
        />
      </Panel>

      <Panel title={strings.eHeading}>
        <p className={HINT}>{strings.eHint}</p>
        <div className="max-w-xs">
          <TextField
            hint={strings.eBlankNote}
            id="rr-e"
            label={strings.eLabel}
            numeric
            onChange={setEText}
            placeholder={strings.ePlaceholder}
            value={eText}
          />
        </div>
      </Panel>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {badFields.length > 0 ? (
          <ErrorNotice>
            {t(strings.problemCount, { fields: badFields.join("; ") })}
          </ErrorNotice>
        ) : null}
        {resolved.bad ? <ErrorNotice>{strings.problemE}</ErrorNotice> : null}
        {total === 0 ? (
          <Notice>{strings.prompt}</Notice>
        ) : (
          <>
            <p className="text-foreground text-sm">
              {t(strings.totalLine, { total: fmt(total) })}{" "}
              {e === null
                ? strings.eMissing
                : t(
                    resolved.source === "proportional"
                      ? strings.eUsedProportional
                      : strings.eUsedEntered,
                    {
                      e: fmt(e, { maximumFractionDigits: 3 }),
                      eligible: fmt(eligible),
                      known: fmt(eligible + (counts.ineligible ?? 0)),
                    }
                  )}
            </p>
            <AaporResults labels={labels} rates={rates} />
            <p className={HINT}>{strings.rr3Note}</p>
            <Reporting rates={rates} resolved={resolved} strings={strings} />
          </>
        )}
      </section>
    </div>
  );
}
